import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import {
  Card, BlockStack, InlineGrid, InlineStack, Text, Button, Select,
  TextField, Checkbox, ChoiceList,
} from '@shopify/polaris';
import { PersonIcon, LockIcon, LocationIcon, ProductIcon, CheckIcon } from '@shopify/polaris-icons';

import { updateSettings } from '../../redux/actions/settings';
import { cartLayoutsIndex } from '../../redux/actions/cartLayouts';
import { isDefaultCartLayout } from '../../constants';

import SettingsPage from '../partials/SettingsPage';
import { UI_COLORS } from '../../constants';
import LoadingSpinner from '../partials/LoadingSpinner';
import FeatureCard from '../partials/FeatureCard';

const Settings = ({ settings, submitSettings, cartLayouts, loadCartLayouts }) => {
  const [submitted, setSubmitted] = useState(false);

  const [pageSize, setPageSize] = useState(settings.settings['page_size']);
  const [defaultLayout, setDefaultLayout] = useState(null);
  const [createFulfillmentOnPack, setCreateFulfillmentOnPack] = useState(settings.settings['create_fulfillment_on_pack']);
  // Store-level setting shared by the admin app and warehouse app - the
  // picking/returning screens read it from the batch response to sort
  // items by BPN part
  const [bpnSeparator, setBpnSeparator] = useState(settings.settings['bpn_separator'] || '-');
  const [bpnMetafieldNamespace, setBpnMetafieldNamespace] = useState(settings.settings['bpn_metafield_namespace'] || 'custom');
  const [bpnMetafieldKey, setBpnMetafieldKey] = useState(settings.settings['bpn_metafield_key'] || 'bpn');

  useEffect(() => {
    loadCartLayouts();
  }, [loadCartLayouts]);

  useEffect(() => {
    if(!cartLayouts.isFetching && cartLayouts.status === 'success') {
      setDefaultLayout(cartLayouts.cartLayouts.find(layout => layout.id === settings.settings['default_cart_layout']) || cartLayouts.cartLayouts.find(isDefaultCartLayout));
    }
  }, [cartLayouts, settings.settings]);


  useEffect(() => {
    if(!settings.isFetching && settings.status === 'success') {
      setSubmitted(false);
    };
  }, [settings])

  const newSettingsJson = () => {
    return {
      page_size: pageSize,
      default_cart_layout: defaultLayout?.id,
      create_fulfillment_on_pack: createFulfillmentOnPack ? true : false,
      bpn_metafield_namespace: bpnMetafieldNamespace || 'custom',
      bpn_metafield_key: bpnMetafieldKey || 'bpn',
      // Empty means no separator at all, which makes every character its
      // own part - fall back to the default instead
      bpn_separator: bpnSeparator || '-'
    }
  }

  const submit = () => {
    setSubmitted(true);
    submitSettings(newSettingsJson());
  }

  return(
    <SettingsPage
      title="App Settings"
      description="Manage Pack Perfect settings for this store. Have questions or need some help? Contact us at support@athousandapps.com"
    >
      <FeatureCard
        title="Users"
        description="Manage users and grant permissions for pickers, packers and stock returns."
        buttonText="Manage users"
        image={PersonIcon}
        linkUrl="/users"
        stripeBackground={UI_COLORS.accent}
      />

      <FeatureCard
        title="Warehouse Login Codes"
        description="Manage the login codes warehouse users enter at each location to access the warehouse app."
        buttonText="Manage login codes"
        image={LockIcon}
        linkUrl="/login_codes"
        stripeBackground='#4a6fdc'
      />

      <FeatureCard
        title="Cart Layouts"
        description="Manage batch picking carts. Define tote layouts during order batching for any picking type."
        buttonText="Cart Layouts"
        image={LocationIcon}
        linkUrl="/cart_layouts"
        stripeBackground={UI_COLORS.danger}
      />

      <FeatureCard
        title="Packing Rules"
        description="Manage the packing checklist rules packers see in the warehouse app for products, variants, collections, tags and order shipments."
        buttonText="Packing rules"
        image={ProductIcon}
        linkUrl="/rules"
        stripeBackground='#4a6fdc'
      />

      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">Settings</Text>

          <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
            <BlockStack gap="400">
              <TextField
                label="BPN Separator"
                helpText="The character(s) separating the parts of your BPNs (for example '-' in A1-05-C). Picking and returning use this to sort items by BPN section."
                value={bpnSeparator}
                onChange={setBpnSeparator}
                placeholder="-"
                autoComplete="off"
              />

              <TextField
                label="BPN Metafield Namespace"
                helpText="The Shopify variant metafield namespace holding your BPN values."
                value={bpnMetafieldNamespace}
                onChange={setBpnMetafieldNamespace}
                placeholder="custom"
                autoComplete="off"
              />

              <TextField
                label="BPN Metafield Key"
                helpText="The Shopify variant metafield key holding your BPN values."
                value={bpnMetafieldKey}
                onChange={setBpnMetafieldKey}
                placeholder="bpn"
                autoComplete="off"
              />

              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">Packing</Text>
                <Checkbox
                  label="Create a Shopify fulfillment when a shipment is packed"
                  checked={createFulfillmentOnPack ? true : false}
                  onChange={setCreateFulfillmentOnPack}
                />
              </BlockStack>
            </BlockStack>

            <BlockStack gap="400">
              { cartLayouts.status !== 'success' &&
                <LoadingSpinner size="small" statusText="Loading cart layouts"/>
              }

              { cartLayouts.status === 'success' &&
                <Select
                  label="Default Cart Style"
                  options={cartLayouts.cartLayouts.map((layout) => ({ label: layout.label, value: layout.id }))}
                  value={defaultLayout?.id || ''}
                  onChange={(id) => setDefaultLayout(cartLayouts.cartLayouts.find((layout) => layout.id === id) || null)}
                />
              }

              <ChoiceList
                title="PDF Page Size"
                choices={[
                  { label: 'US Letter', value: 'LETTER' },
                  { label: 'A4', value: 'A4' },
                ]}
                selected={[pageSize || 'LETTER']}
                onChange={(selected) => setPageSize(selected[0])}
              />
            </BlockStack>
          </InlineGrid>

          <InlineStack align="start">
            <Button
              variant="primary"
              icon={CheckIcon}
              disabled={submitted}
              onClick={() => submit()}
            >
              Save Settings
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>
    </SettingsPage>
  )
}

const mapStateToProps = state => ({
  stores: state.stores,
  settings: state.settings,
  cartLayouts: state.cartLayouts,
});

const mapDispatchToProps = (dispatch) => ({
  submitSettings: (newSettings) => dispatch(updateSettings(newSettings)),
  loadCartLayouts: () => dispatch(cartLayoutsIndex()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
